/**
 * 나눔터 자료 공유 API 서비스
 */

import { auth } from '../firebase/config';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const GALLERY_API_URL = `${API_BASE_URL}/api/gallery`;

const getAuthHeaders = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('로그인이 필요합니다.');
  }
  const token = await currentUser.getIdToken(true);
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const getOptionalAuthHeaders = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return { 'Content-Type': 'application/json' };
  }
  const token = await currentUser.getIdToken(true);
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.detail || '요청을 처리하지 못했어요');
    error.status = response.status;
    throw error;
  }
  return response.json();
};

/**
 * 자료를 나눔터에 공유
 */
export const shareToGallery = async (data) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/share`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * 나눔터 자료 목록 조회
 */
export const getGalleryItems = async ({
  contentType,
  schoolLevel,
  subject,
  sort = 'latest',
  page = 1,
  limit = 12,
  search,
  mineOnly,
  favoritesOnly,
} = {}) => {
  const headers = await getOptionalAuthHeaders();
  const params = new URLSearchParams({ sort, page: String(page), limit: String(limit) });
  if (contentType) params.set('content_type', contentType);
  if (schoolLevel) params.set('school_level', schoolLevel);
  if (subject) params.set('subject', subject);
  if (search) params.set('search', search);
  if (mineOnly) params.set('mine_only', 'true');
  if (favoritesOnly) params.set('favorites_only', 'true');

  const response = await fetch(`${GALLERY_API_URL}/items?${params}`, { headers });
  return handleResponse(response);
};

/**
 * 자료 상세 조회
 */
export const getGalleryItem = async (itemId) => {
  const headers = await getOptionalAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/items/${itemId}`, { headers });
  return handleResponse(response);
};

/**
 * 좋아요 토글
 */
export const toggleGalleryLike = async (itemId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/items/${itemId}/like`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
};

/**
 * 즐겨찾기 토글
 */
export const toggleGalleryFavorite = async (itemId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/items/${itemId}/favorite`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
};

/**
 * "이 내용으로 만들기" 사용 카운트
 */
export const markGalleryItemUsed = async (itemId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/items/${itemId}/use`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
};

/**
 * 다운로드 카운트
 */
export const trackGalleryDownload = async (itemId) => {
  const headers = await getOptionalAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/items/${itemId}/download`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
};

/**
 * 본인 공유 자료 제목/설명 수정
 */
export const updateGalleryItem = async (itemId, { title, description }) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/items/${itemId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ title, description }),
  });
  return handleResponse(response);
};

/**
 * 본인 공유 자료 삭제
 */
export const deleteGalleryItem = async (itemId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/items/${itemId}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
};

/**
 * 내 자료 저장 (콘텐츠 생성 시 자동 호출)
 */
export const saveMyContent = async (data) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/my-content`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * 대화 개요가 바뀌면 이미 있는 내 자료 텍스트 칸만 맞춘다. 문서가 없으면 만들지 않는다.
 */
export const syncMyContentOutline = async ({ conversation_id, content_type, outline }) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/my-content/sync-outline`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ conversation_id, content_type, outline }),
  });
  return handleResponse(response);
};

/**
 * 내 자료 제목 수정
 */
export const updateMyContent = async (itemId, { title }) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/my-content/${itemId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ title }),
  });
  return handleResponse(response);
};

/**
 * 내 자료 삭제
 */
export const deleteMyContent = async (itemId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/my-content/${itemId}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
};

/**
 * 내가 만든 자료 조회 (user_content 컬렉션)
 */
export const getMyContent = async ({ contentType, page = 1, limit = 12 } = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (contentType) params.set('content_type', contentType);

  const response = await fetch(`${GALLERY_API_URL}/my-content?${params}`, { headers });
  return handleResponse(response);
};

/**
 * 내 자료 상세 (versions 전체 + 지연 보정)
 */
export const getMyContentItem = async (itemId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${GALLERY_API_URL}/my-content/${itemId}`, { headers });
  return handleResponse(response);
};

/**
 * 랜덤 인기 자료 (로딩 피드, 카테고리 진입용)
 */
export const getRandomGalleryItems = async ({ contentType, limit = 6 } = {}) => {
  const headers = await getOptionalAuthHeaders();
  const params = new URLSearchParams({ limit: String(limit) });
  if (contentType) params.set('content_type', contentType);

  const response = await fetch(`${GALLERY_API_URL}/random?${params}`, { headers });
  return handleResponse(response);
};
